/// <amd-module name="Checks/Approvals/Web/Components/ControlOptions" />
import React = require("react");

import { AccordionComponent } from "Checks/Approvals/Web/Components/AccordionComponent";
import * as Resources from "Checks/Approvals/Web/Resources";

import { ObservableValue, ObservableArray } from "VSS/Core/Observable";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { TextField } from "VSSUI/TextField";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

export interface IControlOptionsProps extends IVssComponentProperties {
    timeout: number;
    onUpdateTimeout: (timeout: number) => void;
    setControlOptionsValidity: (isControlOptionsValid: boolean) => void;
}

export class ControlOptions extends VssComponent<IControlOptionsProps, {}> {

    constructor(props: IControlOptionsProps) {
        super(props);

        this._populateTimeoutUnitDropdownItems();

        if (this.props.timeout) {
            const hours = 60;
            const days = hours * 24;
            if (this.props.timeout === 0) {
                this._timeoutUnitSelection.select(0);
                this._timeoutValueObservable.value = String(0);
            }

            else {
                if (this.props.timeout % days === 0) {
                    this._timeoutUnitSelection.select(0);
                    this._timeoutValueObservable.value = String(this.props.timeout / days);
                }
                else if (this.props.timeout % hours === 0) {
                    this._timeoutUnitSelection.select(1);
                    this._timeoutValueObservable.value = String(this.props.timeout / hours);
                }
                else {
                    this._timeoutUnitSelection.select(2);
                    this._timeoutValueObservable.value = String(this.props.timeout);
                }
            }

            this._updateAndValidateTimeout();
        }
    }

    public render(): JSX.Element {
        return (
            <AccordionComponent
                className={"control-options-section"}
                uniqueKey={"controlOptionsSection"}
                title={Resources.ControlOptionsAccordionHeader}>
                { this._getControlOptionsSection() }
            </AccordionComponent>
        );
    }

    private _getTimeoutInMinutes(): number {
        let multiplier = 1; // for minute

        // days is selected
        if (this._timeoutUnitSelection.selected(0)) {
            multiplier = 60 * 24;
        }
        else if (this._timeoutUnitSelection.selected(1)) {
            multiplier = 60;
        }

        return parseInt(this._timeoutValueObservable.value) * multiplier;
    }

    private _populateTimeoutUnitDropdownItems = (): void => {
        this._timeoutUnitDropdownItems = new ObservableArray<IListBoxItem<string>>();
        this._timeoutUnitDropdownItems.push({
            id: "days",
            text: Resources.Days,
            data: Resources.Days,
            type: ListBoxItemType.Row
        });

        this._timeoutUnitKeyToIndexMapping["days"] = 0;

        this._timeoutUnitDropdownItems.push({
            id: "hours",
            text: Resources.Hours,
            data: Resources.Hours,
            type: ListBoxItemType.Row
        });

        this._timeoutUnitKeyToIndexMapping["hours"] = 1;

        this._timeoutUnitDropdownItems.push({
            id: "minutes",
            text: Resources.Minutes,
            data: Resources.Minutes,
            type: ListBoxItemType.Row
        });

        this._timeoutUnitKeyToIndexMapping["minutes"] = 2;
        this._timeoutUnitSelection.select(0);
    }

    private _onTimeoutValueChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string): void => {
        this._timeoutValueObservable.value = newValue;
        this._updateAndValidateTimeout();
    }

    private _onTimeoutUnitChange = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<string>) => {
        if (item.id && this._timeoutUnitKeyToIndexMapping[item.id] !== null) {
            this._timeoutUnitSelection.select(this._timeoutUnitKeyToIndexMapping[item.id]);
            this._updateAndValidateTimeout();
        }
    }

    private _updateAndValidateTimeout(): void {
        if (this._timeoutValueObservable.value.length === 0) {
            this._timeoutErrorMessage.value = Resources.TimeoutFieldEmptyError;
        }
        else if (parseInt(this._timeoutValueObservable.value).toString() === this._timeoutValueObservable.value) {
            const timeoutInMinutes = this._getTimeoutInMinutes();
            this.props.onUpdateTimeout(timeoutInMinutes);
            this._updateIsTimeoutValidObservable(timeoutInMinutes);
        }
        else {
            this._timeoutErrorMessage.value = Resources.TimeoutNotValidNumberError;
        }
        this.props.setControlOptionsValidity(!this._timeoutErrorMessage.value);
    }

    private _updateIsTimeoutValidObservable(timeoutInMinutes: number): void {
        if (timeoutInMinutes <= 0) {
            this._timeoutErrorMessage.value = Resources.TimeoutNegativeError;
        }
        else if (timeoutInMinutes > 60 * 24 * 30) {
            this._timeoutErrorMessage.value = Resources.TimeoutMaximumValueError;
        }
        else {
            this._timeoutErrorMessage.value = "";
        }
    }

    private _getControlOptionsSection(): JSX.Element {
        return (
            <div className="approvals-timeout-section">
                <div className="approvals-timeout-header">
                    {Resources.TimeoutText}
                </div>
                <Observer errorMessage={this._timeoutErrorMessage}>
                {(observerProps: {errorMessage: string}) => {
                    return (
                    <FormItem 
                        message={!!observerProps.errorMessage && observerProps.errorMessage}
                        error={!!observerProps.errorMessage}>
                        <div className="approvals-timeout-content">
                            <TextField
                                maxLength={10}
                                ariaLabel={Resources.TimeoutText}
                                containerClassName="approval-timeout-textbox-container"
                                inputClassName="approval-timeout-textbox"
                                value={this._timeoutValueObservable}
                                onChange={this._onTimeoutValueChange} />
                            <Dropdown
                                className="approval-timeout-unit-dropdown"
                                ariaLabel={Resources.TimePeriodDropdownLabel}
                                items={this._timeoutUnitDropdownItems}
                                onSelect={this._onTimeoutUnitChange}
                                selection={this._timeoutUnitSelection} />
                            </div>
                    </FormItem>);
                }}
                </Observer>
        </div>);
    }

    private _timeoutValueObservable: ObservableValue<string> = new ObservableValue<string>("0");
    private _timeoutUnitSelection = new DropdownSelection();
    private _timeoutUnitKeyToIndexMapping: { [key: string]: number } = {};
    private _timeoutUnitDropdownItems: ObservableArray<IListBoxItem<string>>;
    private _timeoutErrorMessage: ObservableValue<string> = new ObservableValue<string>("");
}
